/**
 * Qué cifras enseña la portada del admin.
 *
 * Los agregados de producción no cuentan demos (FR-040). Si no hay ningún
 * negocio en producción, enseñar ceros es mentir por omisión: el tráfico está
 * en las demos. Entonces se suman esas, y la pantalla lo dice.
 */
import type { ContadoresVista } from '../components/Contadores'
import type { ConversionVista, FilaNegocio } from '../routes/tabla-negocios'

const CERO: ContadoresVista = {
    total: 0,
    enCurso: 0,
    finalizadas: 0,
    cerradasBien: 0,
    canceladas: 0,
    incidencias: 0,
    escaladas: 0,
    informativas: 0,
    enEspera: 0,
    sinEncaje: 0,
    colgadas: 0,
}

export function sumarContadores(lista: readonly ContadoresVista[]): ContadoresVista {
    return lista.reduce<ContadoresVista>(
        (a, c) => ({
            total: a.total + c.total,
            enCurso: a.enCurso + c.enCurso,
            finalizadas: a.finalizadas + c.finalizadas,
            cerradasBien: a.cerradasBien + c.cerradasBien,
            canceladas: a.canceladas + c.canceladas,
            incidencias: a.incidencias + c.incidencias,
            escaladas: a.escaladas + c.escaladas,
            informativas: a.informativas + c.informativas,
            enEspera: a.enEspera + c.enEspera,
            sinEncaje: a.sinEncaje + c.sinEncaje,
            colgadas: a.colgadas + c.colgadas,
        }),
        CERO,
    )
}

export function conversionDe(c: ContadoresVista): ConversionVista {
    const denominador = c.cerradasBien + c.canceladas + c.escaladas
    return {
        numerador: c.cerradasBien,
        denominador,
        tasa: denominador > 0 ? c.cerradasBien / denominador : null,
    }
}

export interface CifrasPortada {
    totales: ContadoresVista
    conversion: ConversionVista
    soloDemos: boolean
    nProduccion: number
    nDemos: number
    /** Los negocios cuya suma es `totales`. El menú de la portada (034). */
    negociosDelTotal: FilaNegocio[]
}

export function cifrasPortada(data: {
    totales: ContadoresVista
    conversion: ConversionVista
    negocios: FilaNegocio[]
    demos?: { negocios: number; items?: FilaNegocio[] }
}): CifrasPortada {
    const items = data.demos?.items ?? []
    const nProduccion = data.negocios.length
    const nDemos = data.demos?.negocios ?? items.length
    const soloDemos = nProduccion === 0 && nDemos > 0
    if (!soloDemos) {
        return {
            totales: data.totales,
            conversion: data.conversion,
            soloDemos,
            nProduccion,
            nDemos,
            negociosDelTotal: data.negocios,
        }
    }
    const totales = sumarContadores(items.map((i) => i.contadores))
    return {
        totales,
        conversion: conversionDe(totales),
        soloDemos,
        nProduccion,
        nDemos,
        negociosDelTotal: items,
    }
}

export function piePortada(nProduccion: number, nDemos: number): string {
    if (nProduccion === 0 && nDemos > 0) {
        return `${nDemos} ${nDemos === 1 ? 'demo' : 'demos'} · ninguna en producción`
    }
    const prod = `${nProduccion} ${nProduccion === 1 ? 'negocio' : 'negocios'}`
    if (nDemos === 0) return prod
    return `${prod} · ${nDemos} ${nDemos === 1 ? 'demo' : 'demos'}`
}
